// Opschonen (alleen de planner), met de markup van het prototype (OPS_ROOT en OPS_ROW in design/build/prototype.js):
// voorstellen van medewerkers beslissen, afsluiten wat Prod of Test validated draagt, verlopen deadlines herplannen,
// parkeren of afwerken, taken zonder eigenaar toewijzen en (met Clockify) taken zonder schatting schatten. Filters op
// klant (dezelfde keuze als bovenaan) en eigenaar.
// De meter telt actieve taken met eigenaar, geldige deadline en (met Clockify) een schatting; een taak die bewust geen
// Clockify-taak heeft (Koppelingen), heeft geen schatting nodig. Zonder schatting toont de actieve taken die verder in
// orde zijn; de schatting gaat naar de Clockify-taak (die de server maakt als het klantplan gekoppeld is). Bij Zonder
// eigenaar staan de collega's met de meeste vrije uren in de periode van de taak voorop; zonder capaciteit uit
// Clockify wie de minste actieve taken heeft.
// Fase geparkeerd: actieve taken in een fase die in Planner zelf op On hold of Waiting staat. Het label van een fase telt
// niet mee voor haar taken (ontwerp "Fases op het bord"); hier zet de planner het recht: de taak parkeren, of in Planner
// het label van de fase weghalen.
// Zonder Project-licentie (me.canWrite) zijn de lijsten alleen-lezen met een link naar Planner; afwijzen kan wel, en
// schatten ook (dat gaat naar Clockify, niet naar Planner).
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { api } from "../lib/api";
import { clockifyApi, fmtHours, freeHours, useCapacity, useClockifyLive, useClockifyWrite, type ClockifyLive } from "../lib/api-clockify";
import { proposalApi, type ApproveResult } from "../lib/api-proposals";
import { catOf, fridays, labelColor, labelFor } from "../lib/board";
import { count, dayDate, endOps, isOpen, parkOps, reopenOps, when } from "../lib/cleanup";
import { faseChain, faseParked, padShort } from "../lib/fases";
import { CAP_DEFAULT, hoursText, klantHealth } from "../lib/health";
import { useIndex, useMe, usePlanning, useProposals, useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { addDays, ddmm, deadline, diffDays, plannerTaskUrl } from "../lib/planning";
import type { Fase, Me, Person, PlanningData, Proposal, Task, TaskOp } from "../lib/types";
import { TaskDrawer } from "../components/TaskDrawer";
import { Av } from "../components/Avatar";
import { useAppState } from "../shell/AppState";
import { LoadError } from "../shell/Layout";
import { useToast } from "../shell/Toast";

type Tab = "voorstellen" | "afsluiten" | "verlopen" | "eigenaar" | "fase" | "schatting";
const TABS: [Tab, string][] = [["voorstellen", "Voorstellen"], ["afsluiten", "Klaar om af te sluiten"], ["verlopen", "Verlopen deadline"], ["eigenaar", "Zonder eigenaar"], ["fase", "Fase geparkeerd"], ["schatting", "Zonder schatting"]];
const EMPTY: Record<Tab, string> = {
  voorstellen: "Geen openstaande voorstellen.", afsluiten: "Niets meer af te sluiten.", verlopen: "Geen verlopen deadlines meer.", eigenaar: "Elke actieve taak heeft een eigenaar.",
  fase: "Geen actieve taken in een fase die op On hold of Waiting staat.",
  schatting: "Elke actieve taak met eigenaar en geldige deadline heeft een schatting.",
};
const NONE = "__none";
const pctOf = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const PROGRESS_1: TaskOp[] = [{ kind: "progress", progress: 1 }];
const EST_PICKS = [4, 8, 16, 24, 40];

/** Heeft de taak een schatting? null zonder Clockify: dan telt de schatting niet mee. */
type HasEst = ((t: Task) => boolean) | null;

interface Lists { act: Task[]; props: { p: Proposal; t: Task }[]; close: Task[]; late: Task[]; noOwner: Task[]; noEst: Task[]; faseParked: { t: Task; f: Fase }[]; parkedNoOwner: number }

/** De lijsten binnen de filter. Een verlopen taak met een open voorstel staat onder Voorstellen; Zonder schatting
 *  toont enkel taken met eigenaar en geldige deadline (de rest lost u eerst in de andere tabbladen op). */
function lists(data: PlanningData, proposals: Proposal[], inScope: (t: Task) => boolean, klantName: (id: string) => string, hasEst: HasEst, fases: Map<string, Fase>): Lists {
  const open = data.tasks.filter((t) => isOpen(t) && inScope(t));
  const act = open.filter((t) => catOf(t) === "actief");
  const byId = new Map(act.map((t) => [t.id, t]));
  const props = proposals.flatMap((p) => { const t = byId.get(p.taskId); return t ? [{ p, t }] : []; });
  const pend = new Set(props.map((x) => x.t.id));
  const dlOk = (t: Task) => deadline(t.end, data.today).key !== "verlopen";
  return {
    act, props,
    close: open.filter((t) => catOf(t) === "afsluiten").sort((a, b) => klantName(a.projectId).localeCompare(klantName(b.projectId)) || a.title.localeCompare(b.title)),
    late: act.filter((t) => !dlOk(t) && !pend.has(t.id)).sort((a, b) => a.end.localeCompare(b.end)),
    noOwner: act.filter((t) => !t.owners.length),
    noEst: hasEst ? act.filter((t) => t.owners.length > 0 && dlOk(t) && !hasEst(t)).sort((a, b) => a.end.localeCompare(b.end) || a.title.localeCompare(b.title)) : [],
    faseParked: act.flatMap((t) => { const f = faseParked(faseChain(t.faseId, fases)); return f ? [{ t, f }] : []; })
      .sort((a, b) => klantName(a.t.projectId).localeCompare(klantName(b.t.projectId)) || a.f.title.localeCompare(b.f.title) || a.t.title.localeCompare(b.t.title)),
    parkedNoOwner: open.filter((t) => catOf(t) === "geparkeerd" && !t.owners.length).length,
  };
}

function stats(L: Lists, today: string, hasEst: HasEst) {
  const own = (t: Task) => t.owners.length > 0, dl = (t: Task) => deadline(t.end, today).key !== "verlopen", est = (t: Task) => !hasEst || hasEst(t);
  const rel = L.act.filter((t) => own(t) && dl(t) && est(t)).length;
  return {
    n: L.act.length, own: L.act.filter(own).length, dl: L.act.filter(dl).length, est: L.act.filter(est).length, rel, relPct: pctOf(rel, L.act.length),
    props: L.props.length, close: L.close.length, late: L.late.length, noOwner: L.noOwner.length, faseParked: L.faseParked.length, noEst: L.noEst.length,
  };
}

/** Schatting invullen: snelle keuzes of een eigen aantal uren (Enter of Bewaren). */
function EstEntry({ busy, onSave }: { busy: boolean; onSave: (h: number) => void }) {
  const [v, setV] = useState("");
  const n = Number(v.trim().replace(",", "."));
  const ok = v.trim() !== "" && Number.isFinite(n) && n > 0 && n <= 2000;
  return (
    <>
      {EST_PICKS.map((h) => <button key={h} type="button" className="p-tog p-tog-s" disabled={busy} onClick={() => onSave(h)}>{h} u</button>)}
      <input className="ex-input p-est-in" inputMode="decimal" placeholder="ander" aria-label="Schatting in uren" value={v} disabled={busy}
        onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && ok) onSave(n); }} />
      {ok && <button type="button" className="ex-btn-xs ex-btn-xs--primary" disabled={busy} onClick={() => onSave(n)}>Bewaren</button>}
    </>
  );
}
type Stats = ReturnType<typeof stats>;

/** Goedkeuren of afwijzen, met één melding en Ongedaan maken (datum terug in Planner, voorstel weer open). */
function useDecide() {
  const qc = useQueryClient();
  const toast = useToast();
  const putTask = (t: Task) => qc.setQueryData<PlanningData>(["planning"], (d) => (d ? { ...d, tasks: d.tasks.map((x) => (x.id === t.id ? t : x)) } : d));
  return async (list: Proposal[], approve: boolean, message: string) => {
    const ids = new Set(list.map((p) => p.id));
    qc.setQueryData<Proposal[]>(["proposals"], (ps) => ps?.filter((p) => !ids.has(p.id)));
    const rs = await Promise.allSettled(list.map((p) => (approve ? proposalApi.approve(p.id) : proposalApi.reject(p.id))));
    const done: number[] = [], changes: number[] = [];
    let err = "";
    rs.forEach((r, i) => {
      if (r.status === "rejected") { err = err || (r.reason instanceof Error ? r.reason.message : "Er ging iets mis"); return; }
      done.push(list[i].id);
      const v: Partial<ApproveResult> = r.value; // afwijzen geeft geen taak en geen wijziging terug
      if (v.task) putTask(v.task);
      if (v.changeId) changes.push(v.changeId);
    });
    qc.invalidateQueries({ queryKey: ["proposals"] });
    if (!done.length) { toast.flash(err, { error: true }); return; }
    const undo = async () => {
      const back = await Promise.allSettled(changes.map((id) => api.undo(id)));
      back.forEach((r) => r.status === "fulfilled" && putTask(r.value.task));
      const re = await Promise.allSettled(done.map((id) => proposalApi.reopen(id)));
      qc.invalidateQueries({ queryKey: ["proposals"] });
      const bad = [...back, ...re].filter((r) => r.status === "rejected").length;
      toast.flash(bad ? "Ongedaan maken deels mislukt" : "Ongedaan gemaakt", { error: bad > 0 });
    };
    const failed = list.length - done.length;
    toast.flash(failed ? message + " · " + failed + " mislukt" : message, { undo });
  };
}

interface RowProps {
  t: Task; sub: string; today: string; onOpen: () => void;
  check?: { on: boolean; toggle: () => void };
  /** undefined: geen kolom eigenaar; null: zonder eigenaar */
  owner?: { id?: string; init: string; name: string } | null;
  lbl?: boolean; dl?: boolean; prop?: { text: string; was: string }; mod?: string;
  /** uren uit Clockify (kolom met klokje) */
  hrs?: ReturnType<typeof hoursText>;
  /** een wijziging (schatting) wordt bewaard */
  busy?: boolean;
  below?: ReactNode; inline?: ReactNode;
}

function Row({ t, sub, today, onOpen, check, owner, lbl, dl, prop, mod, hrs, busy, below, inline }: RowProps) {
  const l = lbl ? labelFor(t) : null, d = deadline(t.end, today);
  return (
    <div className={"p-orow" + (t.pending || busy ? " pending" : "")}>
      {check && <label className="p-ochk"><input type="checkbox" checked={check.on} aria-label="Selecteren" onChange={check.toggle} /></label>}
      <div className="p-orow-main">
        <button type="button" className="p-orow-t" onClick={onOpen}><b>{t.title}</b><span>{sub}</span></button>
        {below && <span className="p-oact">{below}</span>}
      </div>
      {owner !== undefined && <span className="p-oown">{owner ? <><Av id={owner.id} init={owner.init} cls="p-av s" /><span>{owner.name}</span></> : <span className="t-mute">geen</span>}</span>}
      <span className="p-ometa">
        {l && <span className="p-lbl"><i style={{ background: labelColor(l) }} />{l}</span>}
        {dl && <span className={"p-dl" + (d.tone ? " t-" + d.tone : "")}><Icon name={d.key === "verlopen" ? "alert" : "cal"} size={13} />{d.text}</span>}
        {prop && <span className="p-oprop"><b>{prop.text}</b><small>{prop.was}</small></span>}
      </span>
      {hrs && <span className="p-ohrs"><span className={hrs.cls} title={hrs.title}><Icon name="clock" size={13} />{hrs.text}</span></span>}
      {mod !== undefined && <span className="p-omod">{mod}</span>}
      {inline && <span className="p-oact">{inline}</span>}
    </div>
  );
}

const InPlanner = ({ me, t }: { me: Me; t: Task }) => (
  <a className="ex-btn-xs" href={plannerTaskUrl(me, t)} target="_blank" rel="noreferrer" title="Deze taak openen in Planner"><Icon name="ext" size={13} />Openen in Planner</a>
);

export function Opschonen() {
  const { data, error, refetch } = usePlanning();
  const { data: me } = useMe();
  const { data: proposals } = useProposals();
  const idx = useIndex(data);
  const { klant, setKlant } = useAppState();
  const ops = useTaskOps();
  const toast = useToast();
  const decide = useDecide();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>("voorstellen");
  const [ownerF, setOwnerF] = useState<string | null>(null);
  const [dd, setDd] = useState<"klant" | "eigenaar" | null>(null);
  const [pick, setPick] = useState<Record<string, boolean>>({});
  const [unpick, setUnpick] = useState<Record<string, boolean>>({});
  const base = useRef<Stats | null>(null);
  const sel = params.get("taak");
  const qc = useQueryClient();
  const { data: liveData, error: liveErr } = useClockifyLive();
  const { data: cap } = useCapacity(me?.role === "planner");
  const cw = useClockifyWrite();
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  if (me && me.role !== "planner") return <Navigate to="/mijn-werk" replace />;
  if (!data && error) return <LoadError what="De planningsdata" onRetry={() => refetch()} />;
  if (!data || !me) return <div className="p-loading">Planningsdata laden…</div>;
  const today = data.today, write = me.canWrite, fr = fridays(today);

  // Clockify: zonder koppeling geen schatting in de meter en geen tabblad Zonder schatting
  const live = liveData && liveData.clockify.state !== "uit" ? liveData : null;
  const hasEst: HasEst = live ? (t) => { const h = live.tasks[t.id]; return !!h && (!!h.none || (h.estimate ?? 0) > 0); } : null;
  const tabs = live ? TABS : TABS.filter(([k]) => k !== "schatting");
  const tabNow: Tab = !live && tab === "schatting" ? "voorstellen" : tab;

  const kName = (id: string) => idx.klanten.get(id)?.name || "";
  const inKlant = (t: Task) => !klant || t.projectId === klant;
  const ownerIs = (t: Task, v: string | null) => v === null || (v === NONE ? !t.owners.length : t.owners.some((o) => o.personId === v));
  const L = lists(data, proposals || [], (t) => inKlant(t) && ownerIs(t, ownerF), kName, hasEst, idx.fases);
  const S = stats(L, today, hasEst);
  // Deze ronde: alles, ook buiten de filter, bij het openen van het scherm en nu (pas als ook Clockify er is)
  const S0 = stats(lists(data, proposals || [], () => true, kName, hasEst, idx.fases), today, hasEst);
  if (!base.current && proposals && (liveData || liveErr)) base.current = S0;
  const B = base.current || S0;

  const counts: Record<Tab, number> = { voorstellen: L.props.length, afsluiten: L.close.length, verlopen: L.late.length, eigenaar: L.noOwner.length, fase: L.faseParked.length, schatting: L.noEst.length };
  const total = tabs.reduce((a, [k]) => a + counts[k], 0);
  const resetSel = () => { setPick({}); setUnpick({}); };
  const openTask = (id: string) => setParams((p) => { p.set("taak", id); return p; });
  const closeTask = () => setParams((p) => { p.delete("taak"); return p; });
  const openFase = (id: string) => setParams((p) => { p.delete("taak"); p.set("fase", id); return p; });
  const ownerOf = (t: Task) => {
    const o = t.owners[0], p = o ? idx.people.get(o.personId) : null;
    if (!o) return null;
    return { id: o.personId, init: p?.initials || "?", name: (p?.first || "Onbekend") + (t.owners.length > 1 ? " +" + (t.owners.length - 1) : "") };
  };
  const sub = (t: Task) => [kName(t.projectId), idx.buckets.get(t.bucketId)?.name, padShort(faseChain(t.faseId, idx.fases), 48) || null].filter(Boolean).join(" · ");
  const open = (t: Task) => () => openTask(t.id);

  // acties: elke actie is één melding met Ongedaan maken
  const approve = (list: { p: Proposal; t: Task }[]) => decide(list.map((x) => x.p), true, list.length === 1 ? "Goedgekeurd: deadline " + dayDate(list[0].p.end) + " voor " + list[0].t.title : list.length + " voorstellen goedgekeurd");
  const reject = (x: { p: Proposal; t: Task }) => decide([x.p], false, "Afgewezen: " + x.t.title);
  const closeTasks = (ts: Task[]) => { resetSel(); ops.runMany(ts.map((t) => ({ task: t, ops: PROGRESS_1 })), ts.length === 1 ? "1 taak afgesloten in Planner" : ts.length + " taken afgesloten in Planner"); };
  const dropClose = (ts: Task[]) => {
    const items = ts.map((t) => ({ task: t, ops: reopenOps(t) })).filter((i) => i.ops.length);
    resetSel();
    if (items.length) ops.runMany(items, "Label weggehaald bij " + count(items.length) + ". Ze staan weer bij het actieve werk.");
  };
  const newDates = (ts: Task[], f: string) => { resetSel(); ops.runMany(ts.map((t) => ({ task: t, ops: endOps(t, f) })), ts.length === 1 ? "Nieuwe deadline " + dayDate(f) + ": " + ts[0].title : count(ts.length) + " naar " + dayDate(f)); };
  const park = (ts: Task[]) => {
    const items = ts.flatMap((t) => { const o = parkOps(t, data); return o ? [{ task: t, ops: o }] : []; });
    const skip = ts.length - items.length;
    if (!items.length) { toast.flash("Deze klantplannen hebben geen label On hold of Waiting in Planner", { error: true }); return; }
    resetSel();
    ops.runMany(items, count(items.length) + " geparkeerd" + (skip ? " · " + skip + " zonder label On hold of Waiting in hun plan" : ""));
  };
  const finish = (ts: Task[]) => { resetSel(); ops.runMany(ts.map((t) => ({ task: t, ops: PROGRESS_1 })), count(ts.length) + " afgewerkt"); };
  const assign = (t: Task, p: Person) => ops.run(t, [{ kind: "owner", personId: p.id, on: true }], "Toegewezen aan " + p.first + ": " + t.title).catch(() => {});
  // schatting naar Clockify; de rij verdwijnt zodra de nieuwe uren er zijn
  const estimate = (t: Task, h: number) => {
    setSaving((s) => ({ ...s, [t.id]: true }));
    cw.run(() => clockifyApi.estimate(t.id, h))
      .then((r) => { if (r.hours) qc.setQueryData<ClockifyLive>(["clockify-live"], (l) => (l ? { ...l, tasks: { ...l.tasks, [t.id]: r.hours! } } : l)); })
      .catch(() => {})
      .finally(() => setSaving((s) => { const n = { ...s }; delete n[t.id]; return n; }));
  };
  const planLinked = (t: Task) => !!live && (!!live.plans[t.projectId] || !!live.tasks[t.id]?.clockifyTaskId);
  const hrs = (t: Task) => (live ? hoursText(live.tasks[t.id]) : undefined);

  // per tabblad: kop, bulkknoppen en rijen
  let title = "", subText = "", all: { on: boolean; toggle: () => void } | null = null, bulk: ReactNode = null, rows: ReactNode[] = [];
  if (tabNow === "voorstellen") {
    title = "Voorstellen van medewerkers";
    subText = "Nieuwe deadlines die medewerkers vanuit Mijn werk voorstellen. Goedkeuren zet de datum in Planner.";
    bulk = write && L.props.length > 1 && <button type="button" className="ex-btn ex-btn--primary" onClick={() => approve(L.props)}>Alles goedkeuren</button>;
    rows = L.props.map((x) => {
      const by = idx.people.get(x.p.by), od = diffDays(x.t.end, today);
      return (
        <Row key={x.p.id} t={x.t} today={today} onOpen={open(x.t)} sub={kName(x.t.projectId) + " · " + when(x.p.at, today)}
          owner={{ id: x.p.by, init: by?.initials || "?", name: by?.first || "Onbekend" }}
          prop={{ text: dayDate(x.p.end), was: "was " + ddmm(x.t.end) + (od > 0 ? ", " + od + " d geleden" : "") }}
          inline={<>
            {write && <button type="button" className="ex-btn-xs ex-btn-xs--primary" onClick={() => approve([x])}>Goedkeuren</button>}
            <button type="button" className="ex-btn-xs" onClick={() => reject(x)}>Afwijzen</button>
            {!write && <InPlanner me={me} t={x.t} />}
          </>} />
      );
    });
  } else if (tabNow === "afsluiten") {
    const chosen = L.close.filter((t) => !unpick[t.id]), on = chosen.length === L.close.length && L.close.length > 0;
    title = "Label Prod of Test validated, maar nog open";
    subText = "Afsluiten zet ze op 100 % in Planner.";
    if (write && L.close.length) all = { on, toggle: () => setUnpick(on ? Object.fromEntries(L.close.map((t) => [t.id, true])) : {}) };
    bulk = write && chosen.length > 0 && <>
      <button type="button" className="ex-btn" onClick={() => dropClose(chosen)}>Label weghalen</button>
      <button type="button" className="ex-btn ex-btn--primary" onClick={() => closeTasks(chosen)}>{chosen.length} afsluiten</button>
    </>;
    rows = L.close.map((t) => (
      <Row key={t.id} t={t} today={today} onOpen={open(t)} sub={sub(t)} owner={ownerOf(t)} lbl hrs={hrs(t)} mod={t.modified ? "gewijzigd " + ddmm(t.modified) : ""}
        check={write ? { on: !unpick[t.id], toggle: () => setUnpick({ ...unpick, [t.id]: !unpick[t.id] }) } : undefined}
        inline={!write && <InPlanner me={me} t={t} />} />
    ));
  } else if (tabNow === "verlopen") {
    const chosen = L.late.filter((t) => pick[t.id]), on = chosen.length === L.late.length && L.late.length > 0;
    title = "Deadline meer dan 30 dagen voorbij";
    subText = "Kies per taak een nieuwe vrijdag, of selecteer er meerdere." + (L.props.length ? " " + count(L.props.length) + " met een voorstel staan onder Voorstellen." : "");
    if (write && L.late.length) all = { on, toggle: () => setPick(on ? {} : Object.fromEntries(L.late.map((t) => [t.id, true]))) };
    bulk = write && chosen.length > 0 && <>
      <button type="button" className="ex-btn ex-btn--primary" onClick={() => newDates(chosen, fr[1])}>{chosen.length} op vrijdag {ddmm(fr[1])}</button>
      <button type="button" className="ex-btn" onClick={() => park(chosen)}>Parkeren</button>
      <button type="button" className="ex-btn" onClick={() => finish(chosen)}>Afwerken</button>
    </>;
    rows = L.late.map((t) => (
      <Row key={t.id} t={t} today={today} onOpen={open(t)} sub={sub(t)} owner={ownerOf(t)} dl
        check={write ? { on: !!pick[t.id], toggle: () => setPick({ ...pick, [t.id]: !pick[t.id] }) } : undefined}
        below={write ? fr.map((f) => <button key={f} type="button" className="p-tog p-tog-s" onClick={() => newDates([t], f)}>vr {ddmm(f)}</button>) : <InPlanner me={me} t={t} />} />
    ));
  } else if (tabNow === "eigenaar") {
    const pool = data.people.filter((p) => p.resourceId);
    const capOf = cap && live ? new Map(cap.people.filter((c) => c.clockifyUserId).map((c) => [c.personId, c])) : null;
    // met capaciteit: de meeste vrije uren in de periode van de taak (van vandaag of de start tot de deadline, minstens
    // een werkweek, zodat een deadline morgen niet iedereen op nul zet); zonder: wie de minste actieve taken heeft
    const actAll = data.tasks.filter((t) => isOpen(t) && catOf(t) === "actief");
    const fewest = pool.map((p) => ({ p, n: actAll.filter((t) => t.owners.some((o) => o.personId === p.id)).length }))
      .sort((a, b) => a.n - b.n || a.p.name.localeCompare(b.p.name)).slice(0, 3).map((x) => ({ ...x, label: x.n + " actief" }));
    const candsFor = (t: Task) => {
      if (!capOf || !cap) return fewest;
      const from = t.start > today ? t.start : today, to = t.end > addDays(from, 4) ? t.end : addDays(from, 4);
      return pool.flatMap((p) => { const c = capOf.get(p.id); return c ? [{ p, n: freeHours(c, cap.weeks, from, to, CAP_DEFAULT) }] : []; })
        .sort((a, b) => b.n - a.n || a.p.name.localeCompare(b.p.name)).slice(0, 3).map((x) => ({ ...x, label: fmtHours(x.n, 0) + " vrij" }));
    };
    const pk = L.parkedNoOwner;
    title = "Actieve taken zonder eigenaar";
    subText = (capOf ? "De knoppen tonen wie in de periode van de taak de meeste vrije uren heeft (capaciteit min geplande uren uit Clockify)." : "De knoppen tonen wie de minste actieve taken heeft.")
      + " Een medewerker kan zo'n taak ook zelf oppakken, alleen voor zichzelf."
      + (pk ? (pk === 1 ? " Ook 1 geparkeerde taak heeft geen eigenaar." : " Ook " + pk + " geparkeerde taken hebben geen eigenaar.") : "");
    rows = L.noOwner.map((t) => (
      <Row key={t.id} t={t} today={today} onOpen={open(t)} sub={sub(t)} dl hrs={hrs(t)}
        below={write ? candsFor(t).map((c) => <button key={c.p.id} type="button" className="p-tog p-tog-s" onClick={() => assign(t, c.p)}>{c.p.first} · {c.label}</button>) : <InPlanner me={me} t={t} />} />
    ));
  } else if (tabNow === "fase") {
    const chosen = L.faseParked.filter((x) => pick[x.t.id]).map((x) => x.t), on = chosen.length === L.faseParked.length && L.faseParked.length > 0;
    title = "Fase op On hold of Waiting, taken nog actief";
    subText = "In Planner staat de fase geparkeerd, maar deze taken staan nog bij het actieve werk. Parkeer de taak, of haal in Planner het label van de fase weg als er wel aan gewerkt wordt.";
    if (write && L.faseParked.length) all = { on, toggle: () => setPick(on ? {} : Object.fromEntries(L.faseParked.map((x) => [x.t.id, true]))) };
    bulk = write && chosen.length > 0 && <button type="button" className="ex-btn ex-btn--primary" onClick={() => park(chosen)}>{chosen.length} parkeren</button>;
    rows = L.faseParked.map(({ t, f }) => (
      <Row key={t.id} t={t} today={today} onOpen={open(t)} sub={sub(t)} owner={ownerOf(t)} lbl dl
        check={write ? { on: !!pick[t.id], toggle: () => setPick({ ...pick, [t.id]: !pick[t.id] }) } : undefined}
        below={<>
          <span className="p-ohint">Fase <b>{f.title}</b>: {f.labels.map((l) => l.name).join(", ")}</span>
          {write ? <button type="button" className="p-tog p-tog-s" onClick={() => park([t])}>Taak parkeren</button> : <InPlanner me={me} t={t} />}
          <button type="button" className="p-tog p-tog-s" onClick={() => openFase(f.id)}>Fase bekijken</button>
          <a className="ex-btn-xs" href={plannerTaskUrl(me, f)} target="_blank" rel="noreferrer" title="De fase openen in Planner"><Icon name="ext" size={13} />Fase in Planner</a>
        </>} />
    ));
  } else {
    const off = L.noEst.filter((t) => !planLinked(t)).length;
    title = "Actieve taken zonder schatting";
    subText = "Met eigenaar en geldige deadline, maar zonder schatting telt een taak niet mee in de bezetting. De schatting gaat naar de taak in Clockify; heeft de taak er nog geen, dan maakt de app ze."
      + (off ? " " + count(off) + " in een klantplan dat nog niet aan Clockify gekoppeld is." : "");
    rows = L.noEst.map((t) => (
      <Row key={t.id} t={t} today={today} onOpen={open(t)} sub={sub(t)} owner={ownerOf(t)} dl busy={!!saving[t.id]}
        below={planLinked(t)
          ? <EstEntry busy={!!saving[t.id]} onSave={(h) => estimate(t, h)} />
          : <span className="p-ohint">Klantplan niet gekoppeld aan Clockify · <Link className="ex-link" to="/koppelingen">Koppelingen</Link></span>} />
    ));
  }

  // filters
  const ownValue = ownerF === NONE ? "zonder" : ownerF ? idx.people.get(ownerF)?.first || "iemand" : "iedereen";
  const scoped = !!(klant || ownerF);
  const scopeText = "Gefilterd op " + [klant ? "klant " + kName(klant) : "", ownerF ? (ownerF === NONE ? "taken zonder eigenaar" : "eigenaar " + (idx.people.get(ownerF)?.name || "")) : ""].filter(Boolean).join(" en ") + ". Deze ronde telt alles.";
  const klantItems = () => {
    const n = new Map<string, number>();
    for (const t of data.tasks) if (isOpen(t)) n.set(t.projectId, (n.get(t.projectId) || 0) + 1);
    return [{ id: null as string | null, label: "Alle klanten", sub: data.klanten.length + " klantplannen", tone: "ok" },
      ...[...data.klanten].sort((a, b) => a.name.localeCompare(b.name)).map((k) => ({ id: k.id as string | null, label: k.name, sub: (n.get(k.id) || 0) + " open taken", tone: klantHealth(k, data).tone as string }))];
  };
  const ownerItems = () => {
    const inK = data.tasks.filter((t) => isOpen(t) && inKlant(t));
    return [{ label: "Iedereen", v: null as string | null }, ...data.people.map((p) => ({ label: p.name, v: p.id as string | null })), { label: "Zonder eigenaar", v: NONE as string | null }]
      .map((o) => ({ ...o, n: inK.filter((t) => ownerIs(t, o.v)).length }))
      .filter((o) => o.v === null || o.v === NONE || o.n > 0 || o.v === ownerF);
  };

  const meter = (label: string, v: number) => { const p = pctOf(v, S.n); return { label, p, cls: "p-load " + (p < 50 ? "err" : p < 80 ? "warn" : "ok") }; };
  const delta = S0.relPct - B.relPct;
  const round: [string, number, number][] = [["Voorstellen", B.props, S0.props], ["Klaar om af te sluiten", B.close, S0.close], ["Verlopen deadline", B.late, S0.late], ["Zonder eigenaar", B.noOwner, S0.noOwner], ["Fase geparkeerd", B.faseParked, S0.faseParked]];
  if (live) round.push(["Zonder schatting", B.noEst, S0.noEst]);
  const meters = [meter("Met eigenaar", S.own), meter("Geldige deadline", S.dl), ...(live ? [meter("Met schatting", S.est)] : [])];
  const selTask = sel ? data.tasks.find((t) => t.id === sel) : null;

  return (
    <>
      <div className="p-head">
        <div><h1>Opschonen</h1><p>{total} {total === 1 ? "punt vraagt" : "punten vragen"} een beslissing · na elke ronde worden de cijfers op het bord en in de capaciteit betrouwbaarder</p></div>
        <div className="p-tools">
          <div className="p-ddwrap">
            <button type="button" className="ex-btn p-dd" aria-haspopup="listbox" aria-expanded={dd === "klant"} onClick={() => setDd(dd === "klant" ? null : "klant")}>Klant<span className="p-dd-v">{klant ? kName(klant) : "alle"}</span><Icon name="down" size={14} /></button>
            {dd === "klant" && <>
              <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setDd(null)} />
              <div className="p-pop p-pop-s p-pop-k p-pop-r" role="listbox" aria-label="Klant kiezen">
                <div className="p-pop-b">
                  {klantItems().map((o) => (
                    <button key={o.id || "alle"} type="button" className={"p-opt1 p-kopt" + (klant === o.id ? " on" : "")} onClick={() => { setKlant(o.id); setDd(null); resetSel(); }}>
                      <span className={"ex-dot" + (o.tone !== "off" ? " ex-dot--" + o.tone : "")} /><span className="nm">{o.label}</span><span className="c">{o.sub}</span>
                    </button>
                  ))}
                </div>
                <div className="p-pop-f">Zelfde keuze als bovenaan het scherm</div>
              </div>
            </>}
          </div>
          <div className="p-ddwrap">
            <button type="button" className="ex-btn p-dd" aria-haspopup="listbox" aria-expanded={dd === "eigenaar"} onClick={() => setDd(dd === "eigenaar" ? null : "eigenaar")}>Eigenaar<span className="p-dd-v">{ownValue}</span><Icon name="down" size={14} /></button>
            {dd === "eigenaar" && <>
              <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setDd(null)} />
              <div className="p-pop p-pop-s p-pop-r" role="listbox" aria-label="Eigenaar kiezen">
                <div className="p-pop-b">
                  {ownerItems().map((o) => (
                    <button key={o.v || "iedereen"} type="button" className={"p-opt1" + (ownerF === o.v ? " on" : "")} onClick={() => { setOwnerF(o.v); setDd(null); resetSel(); }}>
                      <span className={"p-radio" + (ownerF === o.v ? " on" : "")} /><span className="nm">{o.label}</span><span className="c">{o.n}</span>
                    </button>
                  ))}
                </div>
                <div className="p-pop-f">Open taken binnen de gekozen klant</div>
              </div>
            </>}
          </div>
        </div>
      </div>
      {scoped && (
        <div className="p-fnote"><Icon name="search" size={14} /><span>{scopeText}</span>
          <button type="button" className="ex-link" onClick={() => { setKlant(null); setOwnerF(null); resetSel(); }}>Filters wissen</button>
        </div>
      )}
      <div className="p-tabrow">
        <div className="ex-tabs p-tabs" role="tablist">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" role="tab" className="ex-tab" aria-selected={tabNow === k} onClick={() => { setTab(k); resetSel(); }}>{label}<span className="p-tabn">{counts[k]}</span></button>
          ))}
        </div>
      </div>
      {!write && (
        <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
          <p>Wijzigen doet u in Planner zelf: vanuit de app schrijven kan enkel met een Project-licentie. Een voorstel afwijzen kan hier wel.</p>
        </div>
      )}
      <div className="p-ops">
        <section className="p-card p-ops-list">
          <div className="p-card-h">
            <div style={{ minWidth: 0 }}><h2>{title}</h2><p>{subText}</p></div>
            <div className="p-tools">
              {all && <label className="ex-check p-check"><input type="checkbox" checked={all.on} onChange={all.toggle} />Alles</label>}
              {bulk}
            </div>
          </div>
          <div className="p-ops-body">
            {rows}
            {!rows.length && <p className="p-empty p-mw-empty">{EMPTY[tabNow]}</p>}
          </div>
        </section>
        <div className="p-ops-side">
          <section className="p-card">
            <div className="p-card-h"><div><h2>Betrouwbaarheid planning</h2><p>{live ? "Actieve taken met eigenaar, geldige deadline en schatting" : "Actieve taken met eigenaar en geldige deadline · zonder Clockify telt de schatting niet mee"}</p></div></div>
            <div className="p-rel">
              <div className="p-rel-top"><span className={"p-rel-v " + (S.relPct < 50 ? "t-err" : S.relPct < 80 ? "t-warn" : "t-ok")}>{S.relPct} %</span><span className="p-rel-sub">{S.rel} van {S.n} actieve taken</span></div>
              {delta > 0 && <span className="p-rel-delta">+{delta} punten in deze ronde</span>}
              {meters.map((m) => (
                <div key={m.label} className="p-meter"><span>{m.label}</span><b>{m.p} %</b><span className={m.cls}><i style={{ width: m.p + "%" }} /></span></div>
              ))}
            </div>
          </section>
          <section className="p-card">
            <div className="p-card-h"><div><h2>Deze ronde</h2><p>Aantal bij het openen, en nu</p></div></div>
            <div className="p-round">
              {round.map(([label, a, b]) => <div key={label} className="p-kv"><span>{label}</span><b className={a === b ? "" : "t-ok"}>{a === b ? b : a + " naar " + b}</b></div>)}
            </div>
          </section>
          <div className="ex-notice ex-notice--mist p-notice"><Icon name="clock" size={16} /><p>Vaste ronde op vrijdag, meestal tien minuten.</p></div>
        </div>
      </div>
      {selTask && <TaskDrawer key={selTask.id} task={selTask} data={data} onClose={closeTask} />}
    </>
  );
}
